'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { trackAuth } from '@/lib/analytics';

interface LoginValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface Props {
  onSwitchToRegister: () => void;
}

export default function LoginForm({ onSwitchToRegister }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const { signIn } = useAuth();
  const router = useRouter();

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<LoginValues>();
  const emailValue = watch('email');

  const DEMO_ACCOUNTS = [
    { label: 'Visitor', email: 'visitor@demo.maritzgolf.co.za', password: 'Demo@1234', color: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' },
    { label: 'Member', email: 'member@demo.maritzgolf.co.za', password: 'Demo@1234', color: 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100' },
    { label: 'Admin', email: 'admin@demo.maritzgolf.co.za', password: 'Demo@1234', color: 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100' },
  ];

  const fillDemo = (email: string, password: string) => {
    setValue('email', email, { shouldValidate: true });
    setValue('password', password, { shouldValidate: true });
  };

  const handleForgotPassword = async () => {
    if (!emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      toast.error('Please enter your email address first, then click Forgot Password.');
      return;
    }
    setIsSendingReset(true);
    try {
      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
      const { error } = await supabase.auth.resetPasswordForEmail(emailValue, {
        redirectTo: `${siteUrl}/auth/callback?type=recovery`,
      });
      if (error) throw error;
      toast.success('Password reset email sent! Check your inbox.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsSendingReset(false);
    }
  };

  const onSubmit = async (data: LoginValues) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      trackAuth('login', 'email');
      toast.success(`Welcome back! Signed in as ${data.email}`);

      // Fetch role from user_profiles to determine redirect destination
      let redirectPath = '/member-portal';
      try {
        const supabase = createClient();
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', result?.user?.id)
          .maybeSingle();
        if (profile?.role === 'admin' || profile?.role === 'golf_manager') {
          redirectPath = '/administration-dashboard';
        }
      } catch {
        // Default to member portal if role check fails
      }

      router.push(redirectPath);
      router.refresh();
    } catch (error: any) {
      toast.error(error?.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-md">
      {/* Board Demo Credentials Panel */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 mb-1">
        <p className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1.5">
          <Icon name="BeakerIcon" size={13} variant="outline" />
          Board Demo — click to auto-fill credentials
        </p>
        <div className="grid grid-cols-3 gap-2">
          {DEMO_ACCOUNTS.map((acc) => (
            <button
              key={acc.label}
              type="button"
              onClick={() => fillDemo(acc.email, acc.password)}
              className={`rounded-lg border px-2 py-1.5 text-xs font-semibold transition-colors ${acc.color}`}
            >
              {acc.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-amber-700 mt-2 leading-tight">
          Password for all accounts: <span className="font-mono font-bold">Demo@1234</span>
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
        <input
          type="email"
          className={`input-field ${errors.email ? 'error' : ''}`}
          placeholder="your@email.co.za"
          {...register('email', {
            required: 'Email address is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
      </div>

      <div>
        <div className="flex justify-between items-center mb-1.5">
          <label className="text-sm font-semibold text-foreground">Password</label>
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={isSendingReset}
            className="text-xs text-primary hover:text-secondary font-medium transition-colors disabled:opacity-60"
          >
            {isSendingReset ? 'Sending…' : 'Forgot password?'}
          </button>
        </div>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            className={`input-field pr-11 ${errors.password ? 'error' : ''}`}
            placeholder="Enter your password"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Password must be at least 6 characters' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} variant="outline" />
          </button>
        </div>
        {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
      </div>

      <div className="flex items-center gap-2.5">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input type="checkbox" className="sr-only" {...register('rememberMe')} />
          <div className="w-4 h-4 rounded border-2 border-border flex items-center justify-center" />
          <span className="text-sm text-muted-foreground">Remember me for 30 days</span>
        </label>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full btn-primary py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-70 mt-2"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Signing In...
          </>
        ) : (
          <>
            <Icon name="ArrowRightCircleIcon" size={18} variant="outline" />
            Sign In to My Account
          </>
        )}
      </button>

      <p className="text-center text-sm text-muted-foreground pt-2">
        New to MGC?{' '}
        <button
          type="button"
          onClick={onSwitchToRegister}
          className="text-primary font-semibold hover:text-secondary transition-colors"
        >
          Create your visitor profile
        </button>
      </p>

      <div className="pt-2">
        <Link href="/administration-dashboard" className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <Icon name="Cog6ToothIcon" size={13} variant="outline" />
          Staff / Admin Access
        </Link>
      </div>
    </form>
  );
}